/**
 * MentionInput - a note box where "@" names a teammate (docs/plans/crm-report-builder-search-collab-plan.md
 * A3.3 to A3.5 and B3.5; WP M-6). It is the note textarea of the lead page's composer and of the
 * call-mode scratchpad, and X-3 mounts it for the timeline's Reply and for NoteEditForm's renderInput
 * (its props take NoteEditInputProps as they come).
 *
 *   - Typing "@" at the start, after a space, a line break or an opening bracket or quote opens
 *     MentionPicker with the teammates who can be mentioned (GET /mentionable, read once per session
 *     and first asked for when the box takes the focus), filtered as you type (M-2's detectTrigger and
 *     filterMentionable). The viewer is never offered: mentioning yourself tells nobody (A3.5).
 *   - Arrows move, Enter or Tab picks, a click picks. The pick puts "@Full Name " in place of what was
 *     typed (applyPick), the caret after it, and reports the teammate through onPickedChange.
 *   - Esc closes the list ONLY. It is read in the bubble phase and both stopped and marked handled
 *     (preventDefault), so the composer, the scratchpad, NoteEditForm and the call workspace around
 *     the box stay open (NoteEditForm ignores an event already defaultPrevented). With no list open,
 *     Esc passes through untouched. The "@" whose list Esc closed stays closed until that search ends.
 *   - The list shows only when somebody matches; it stays shut right after a pick (the picked names
 *     go to detectTrigger); once the text names MENTIONS_PER_NOTE_MAX picked teammates it says so
 *     instead of offering more.
 *   - Every key the list does not use goes to the host's onKeyDown (Ctrl/Cmd+Enter to post).
 *   - enabled={false} is a plain text box: no list and no request (the composer's text and email
 *     modes).
 *   - Where the list opens (placement 'auto', the default): above the box when there is room for it
 *     there, else below, else on the roomier side with the list made shorter to fit. The room is
 *     measured when the list opens, inside the visible viewport and every clipping or scrolling
 *     ancestor (the conversation card, the thread, the call workspace), so a box docked at the
 *     bottom of a card opens upward, a note being edited near the top of the thread opens downward,
 *     and the call-mode pad that fills a phone's screen keeps its list on screen. 'above' and
 *     'below' fix the side.
 *
 * The text and the picked list belong to the host (both controlled). At Post the host sends
 * extractMentions(text, picked) (namedAtPost below): only the teammates whose "@Name" is still in the
 * text, so a name edited out before Post tells nobody.
 */
import { forwardRef, useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import type { ChangeEvent, FocusEvent, KeyboardEvent, MutableRefObject, SyntheticEvent } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { listMentionable } from './api';
import MentionPicker from './MentionPicker';
import type { MentionPlacement } from './MentionPicker';
import {
  MENTIONS_PER_NOTE_MAX,
  MENTION_PICKER_LIMIT,
  applyPick,
  detectTrigger,
  extractMentions,
  filterMentionable,
} from './mentionLogic';
import type { MentionableUser } from './types';

export type { MentionPlacement } from './MentionPicker';

export interface MentionInputProps {
  /** The text (controlled). */
  value: string;
  /** Every change of the text: typing, and a pick (which replaces the typed "@..." with "@Full Name "). */
  onChange: (next: string) => void;
  /**
   * The teammates picked so far (controlled). Start empty for a new note; seed an edit with
   * pickedFromNote(note.metadata) so a kept "@Name" stays a mention.
   */
  picked: ReadonlyArray<MentionableUser>;
  /** A pick: the list as it now is (the teammate added once; a teammate already picked is not repeated). */
  onPickedChange: (next: MentionableUser[]) => void;
  /** False: a plain text box, with no list and no request. Default true. */
  enabled?: boolean;
  /** Where the list opens: 'auto' (the default; above when it fits, else below, measured) or a fixed side. */
  placement?: 'auto' | MentionPlacement;
  /** Never offered; default the signed-in user (mentioning yourself tells nobody). */
  viewerId?: string | null;
  /** The teammates to offer; default GET /mentionable through listMentionable() (read once per session). */
  users?: ReadonlyArray<MentionableUser> | null;
  id?: string;
  disabled?: boolean;
  /** Take the keyboard when mounted, the caret at the end of the text. */
  autoFocus?: boolean;
  placeholder?: string;
  /** Classes of the textarea. */
  className?: string;
  /** Classes added to the wrapper, which is `relative` (the list is placed against it). */
  wrapperClassName?: string;
  rows?: number;
  maxLength?: number;
  'aria-label'?: string;
  /** Every key the open list does not use (with no list open, every key). */
  onKeyDown?: (e: KeyboardEvent<HTMLTextAreaElement>) => void;
  onFocus?: (e: FocusEvent<HTMLTextAreaElement>) => void;
  onBlur?: (e: FocusEvent<HTMLTextAreaElement>) => void;
}

/** What a note names at Post: the ids to send as `mentions` and those teammates, in the text's order. */
export function namedAtPost(
  text: string,
  picked: ReadonlyArray<MentionableUser>,
): { ids: string[]; users: MentionableUser[] } {
  const ids = extractMentions(text, picked);
  const users: MentionableUser[] = [];
  for (const id of ids) {
    const user = picked.find((u) => u.id === id);
    if (user) users.push(user);
  }
  return { ids, users };
}

/** Where the caret was and in which text: a caret measured on another text is never used. */
interface CaretAt {
  text: string;
  /** Null while a range is selected (no "@" search then). */
  pos: number | null;
}

const caretOf = (box: HTMLTextAreaElement): CaretAt => ({
  text: box.value,
  pos: box.selectionStart === box.selectionEnd ? box.selectionStart : null,
});

/** The room the list takes at its tallest (max-h-56 from sm:) with its frame and gap, in px. */
const LIST_ROOM = 240;

/** The shortest list worth showing: two rows at the phone's 44 px. */
const LIST_MIN = 88;

/** The side the list opens on, and a tighter height when neither side has LIST_ROOM. */
interface ListFit {
  side: MentionPlacement;
  maxHeight: number | null;
}

/**
 * Where the list fits around `wrap`: the room above and below it inside the visible viewport (the
 * visual viewport where the browser has one, so an on-screen keyboard counts) and inside every
 * ancestor that clips or scrolls. Above is preferred.
 */
function fitAround(wrap: HTMLElement): ListFit {
  const rect = wrap.getBoundingClientRect();
  const view = window.visualViewport;
  let top = view ? view.offsetTop : 0;
  let bottom = view ? view.offsetTop + view.height : window.innerHeight;
  for (let el = wrap.parentElement; el && el !== document.body; el = el.parentElement) {
    const overflowY = window.getComputedStyle(el).overflowY;
    if (overflowY && overflowY !== 'visible') {
      const clip = el.getBoundingClientRect();
      top = Math.max(top, clip.top);
      bottom = Math.min(bottom, clip.bottom);
    }
  }
  const above = rect.top - top;
  const below = bottom - rect.bottom;
  if (above >= LIST_ROOM) return { side: 'above', maxHeight: null };
  if (below >= LIST_ROOM) return { side: 'below', maxHeight: null };
  const side: MentionPlacement = above >= below ? 'above' : 'below';
  const room = side === 'above' ? above : below;
  // The frame, the gap and a little air come off the room; the list keeps two rows at least.
  return { side, maxHeight: Math.max(LIST_MIN, Math.floor(room - 16)) };
}

const MentionInput = forwardRef<HTMLTextAreaElement, MentionInputProps>(function MentionInput(
  {
    value,
    onChange,
    picked,
    onPickedChange,
    enabled = true,
    placement = 'auto',
    viewerId,
    users,
    id,
    disabled = false,
    autoFocus = false,
    placeholder,
    className,
    wrapperClassName,
    rows,
    maxLength,
    'aria-label': ariaLabel,
    onKeyDown,
    onFocus,
    onBlur,
  },
  ref,
) {
  const { user } = useAuth();
  const viewer = viewerId === undefined ? user?.id ?? null : viewerId;
  const baseId = useId();
  const listId = `${baseId}-mentions`;
  const optionId = useCallback((index: number) => `${baseId}-mention-${index}`, [baseId]);

  const wrapRef = useRef<HTMLDivElement | null>(null);
  const boxRef = useRef<HTMLTextAreaElement | null>(null);
  // The caret a pick asked for, placed once the host has rendered the new text.
  const pendingCaret = useRef<CaretAt | null>(null);
  const [caretAt, setCaretAt] = useState<CaretAt>({ text: '', pos: null });
  const [focused, setFocused] = useState(false);
  // The start of the "@" whose list Esc closed.
  const [dismissed, setDismissed] = useState<number | null>(null);
  // The highlighted row, remembered for one search (its "@" position and query).
  const [active, setActive] = useState<{ key: string; index: number }>({ key: '', index: 0 });
  const [loaded, setLoaded] = useState<MentionableUser[] | null>(null);
  // Where the list fits (placement 'auto'), measured each time a list opens.
  const [fit, setFit] = useState<ListFit>({ side: 'above', maxHeight: null });

  const team = users ?? loaded;
  const live = enabled && focused && !disabled;
  const trigger = live && caretAt.text === value ? detectTrigger(value, caretAt.pos, picked) : null;
  const options =
    trigger && team
      ? filterMentionable(team, trigger.query, { excludeIds: [viewer], limit: MENTION_PICKER_LIMIT })
      : [];
  const shown = trigger !== null && options.length > 0 && trigger.start !== dismissed;
  const full = shown && extractMentions(value, picked).length >= MENTIONS_PER_NOTE_MAX;
  const listOpen = shown && !full;
  const listKey = trigger ? `${trigger.start}:${trigger.query}` : '';
  const activeIndex = listOpen && active.key === listKey ? Math.min(active.index, options.length - 1) : 0;
  const triggerStart = trigger ? trigger.start : null;

  // A closed list reopens once its search has ended (the caret left it, or the box lost the focus).
  useEffect(() => {
    if (dismissed !== null && triggerStart !== dismissed) setDismissed(null);
  }, [dismissed, triggerStart]);

  // Measure where the list fits when it opens (and for each new "@"), before it is painted.
  useLayoutEffect(() => {
    const wrap = wrapRef.current;
    if (!shown || placement !== 'auto' || !wrap) return;
    const next = fitAround(wrap);
    setFit((prev) => (prev.side === next.side && prev.maxHeight === next.maxHeight ? prev : next));
  }, [shown, triggerStart, placement]);
  const side: MentionPlacement = placement === 'auto' ? fit.side : placement;
  const listMaxHeight = placement === 'auto' ? fit.maxHeight : null;

  // The teammates, asked for when the box first takes the focus with mentions on. A failure leaves
  // "@" as plain text; the next focus asks again.
  useEffect(() => {
    if (!enabled || !focused || users || loaded) return undefined;
    let alive = true;
    listMentionable().then(
      (list) => {
        if (alive) setLoaded(list);
      },
      () => {
        /* no list: nothing opens; the next focus tries again */
      },
    );
    return () => {
      alive = false;
    };
  }, [enabled, focused, users, loaded]);

  // After a pick: the caret goes after "@Full Name " once the host shows the new text.
  useLayoutEffect(() => {
    const want = pendingCaret.current;
    const box = boxRef.current;
    if (!want || !box || box.value !== want.text) return;
    pendingCaret.current = null;
    const at = want.pos ?? want.text.length;
    try {
      box.setSelectionRange(at, at);
    } catch {
      /* a box that cannot take a selection keeps the browser's caret */
    }
  }, [value]);

  // autoFocus: the keyboard on mount, the caret at the end (NoteEditForm's promise for its box).
  useLayoutEffect(() => {
    const box = boxRef.current;
    if (!autoFocus || !box) return;
    const end = box.value.length;
    try {
      box.setSelectionRange(end, end);
    } catch {
      /* keep the browser's caret */
    }
    box.focus();
    // Read once, on mount, as the autofocus attribute is.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setBox = useCallback(
    (node: HTMLTextAreaElement | null) => {
      boxRef.current = node;
      if (typeof ref === 'function') ref(node);
      else if (ref) (ref as MutableRefObject<HTMLTextAreaElement | null>).current = node;
    },
    [ref],
  );

  const pick = (who: MentionableUser | undefined) => {
    if (!who || !trigger) return;
    const result = applyPick(value, trigger, who);
    if (!result) return;
    const next = { text: result.text, pos: result.caret };
    pendingCaret.current = next;
    setCaretAt(next);
    onChange(result.text);
    if (!picked.some((u) => u.id === result.user.id)) onPickedChange([...picked, result.user]);
  };

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setCaretAt(caretOf(e.target));
    onChange(e.target.value);
  };

  // The caret moved (keys, a click, a selection): the "@" search follows it.
  const handleSelect = (e: SyntheticEvent<HTMLTextAreaElement>) => {
    const next = caretOf(e.currentTarget);
    setCaretAt((prev) => (prev.text === next.text && prev.pos === next.pos ? prev : next));
  };

  const handleFocus = (e: FocusEvent<HTMLTextAreaElement>) => {
    setFocused(true);
    setCaretAt(caretOf(e.currentTarget));
    onFocus?.(e);
  };

  const handleBlur = (e: FocusEvent<HTMLTextAreaElement>) => {
    setFocused(false);
    onBlur?.(e);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (shown && trigger && !e.nativeEvent.isComposing) {
      // Esc closes the list and nothing else: handled here, never seen by the hosts around the box.
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        setDismissed(trigger.start);
        return;
      }
      const plain = !e.ctrlKey && !e.metaKey && !e.altKey && !e.shiftKey;
      if (listOpen && plain) {
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          e.preventDefault();
          e.stopPropagation();
          const n = options.length;
          const index = e.key === 'ArrowDown' ? (activeIndex + 1) % n : (activeIndex - 1 + n) % n;
          setActive({ key: listKey, index });
          return;
        }
        if (e.key === 'Enter' || e.key === 'Tab') {
          e.preventDefault();
          e.stopPropagation();
          pick(options[activeIndex]);
          return;
        }
      }
    }
    onKeyDown?.(e);
  };

  return (
    <div
      ref={wrapRef}
      data-mention-input=""
      className={wrapperClassName ? `relative ${wrapperClassName}` : 'relative'}
    >
      <textarea
        ref={setBox}
        id={id}
        value={value}
        onChange={handleChange}
        onSelect={handleSelect}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        onBlur={handleBlur}
        disabled={disabled}
        placeholder={placeholder}
        rows={rows}
        maxLength={maxLength}
        aria-label={ariaLabel}
        role={enabled ? 'combobox' : undefined}
        aria-autocomplete={enabled ? 'list' : undefined}
        aria-expanded={enabled ? listOpen : undefined}
        aria-controls={listOpen ? listId : undefined}
        aria-activedescendant={listOpen ? optionId(activeIndex) : undefined}
        className={className}
      />
      {shown && (
        <MentionPicker
          id={listId}
          users={options}
          activeIndex={activeIndex}
          optionId={optionId}
          onPick={pick}
          onActiveChange={(index) => setActive({ key: listKey, index })}
          placement={side}
          maxHeight={listMaxHeight}
          full={full}
        />
      )}
    </div>
  );
});

export default MentionInput;
